import { useLingui } from "@lingui/react/macro";
import { Anchor, Box, List, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocImage } from "@/components/docs/DocImage.tsx";
import { DocsContent } from "@/components/docs/DocsContent.tsx";
import costBearerEn from "@/docs/images/property-search/cost-bearer.en.webp";
import costBearerSv from "@/docs/images/property-search/cost-bearer.sv.webp";
import costEn from "@/docs/images/property-search/cost.en.webp";
import costSv from "@/docs/images/property-search/cost.sv.webp";
import respEn from "@/docs/images/property-search/responsibility.en.webp";
import respSv from "@/docs/images/property-search/responsibility.sv.webp";

export const Route = createFileRoute(
  "/docs/property-search/specify-data-extract",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useLingui();

  return (
    <DocsContent>
      <Title order={1} size="h2">{t`Specify data extract`}</Title>
      <Text>
        {t`This is the second of the property search process. Different searches may have different costs and return different amounts of information. This step
        includes setting the cost bearer and dataset which will affect the information in the property details and costs.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Cost Bearer`}</DocHeading>
      <Text>
        {t`All REALity requests need to be connected to either a Sweco project or result unit. This connection is mainly for cost allocation and
        traceability. Choose a project by searching for a project name or number, and select it from the list. The project needs to be registered
        on the REALity platform. If you don't find the correct project, you can either enter a complete project number and click "Create new project: X” in the dropdown, or `}
        <Anchor href="/projects/create">{t`create a new project`}</Anchor>
        {t`. Read more about this on the `}
        <Anchor href="/docs/getting-started/projects">{t`Projects`}</Anchor>
        {t` page in the documentation.`}
      </Text>

      <DocImage
        src={{ sv: costBearerSv, en: costBearerEn }}
        alt={t`Cost bearer`}
        height={400}
      />

      <DocHeading order={3} size="h4">{t`Why the project matters`}</DocHeading>
      <Text>{t`The project connection may be used for:`}</Text>
      <List withPadding>
        <List.Item>{t`Cost allocation`}</List.Item>
        <List.Item>{t`Customer-specific access to datasets`}</List.Item>
        <List.Item>{t`Licensing and permitted-use checks`}</List.Item>
        <List.Item>{t`Reporting and auditing`}</List.Item>
        <List.Item>{t`Understanding the business purpose of the request`}</List.Item>
        <List.Item>{t`Make data extractions via the customers Geodatasamverkan`}</List.Item>
      </List>

      <Box bg="sand.0" bd="1px solid sand.4" bdrs="md" p="md" maw="900px">
        <Text>
          {t`Using the wrong project can result in incorrect cost allocation and may break licensing or customer-specific conditions.`}
        </Text>
      </Box>

      <DocHeading order={3} size="h4">{t`Future improvements`}</DocHeading>
      <Text>
        {t`The long-term goal is to make project selection easier by connecting REALity to Sweco’s project and customer information. This is intended to be more reliable than entering a project number as free text.`}
      </Text>

      <DocHeading order={2} size="h3">{t`Dataset`}</DocHeading>
      <Text>
        {t`Under Dataset, specify which information you want to retrieve in the property extract. To make a complete extract containing all data, select the top
        checkbox. Otherwise, select the datasets you are interested in. Read more about the information contained in each dataset under `}
        <Anchor href="/docs/property-search/property-information">{t`Viewing property information`}</Anchor>
        {t` and the associated costs under `}
        <Anchor href="/docs/property-search/costs">{t`Cost overview`}</Anchor>
        {t`.`}
      </Text>

      <DocHeading
        order={3}
        size="h4"
      >{t`Why choose a focused search?`}</DocHeading>
      <Text>{t`Selecting a smaller or more focused search can:`}</Text>
      <List withPadding>
        <List.Item>{t`Reduce unnecessary data requests`}</List.Item>
        <List.Item>{t`Reduce costs`}</List.Item>
        <List.Item>{t`Make the result easier to interpret`}</List.Item>
        <List.Item>{t`Avoid processing information that is not needed.`}</List.Item>
      </List>

      <DocHeading order={2} size="h3">{t`Responsibility`}</DocHeading>
      <Text>
        {t`To make a property extract, you must accept Lantmäteriet’s terms of use and confirm compliance with the GDPR and FRL. Tick the boxes to make the 'Buy data' button clickable.`}
      </Text>
      <DocImage
        src={{ sv: respSv, en: respEn }}
        alt={t`Screenshot of Responsibility section`}
        height={300}
      />
      <DocHeading order={2} size="h3">{t`Cost for withdrawal`}</DocHeading>
      <Text>
        {t`The price for the total extraction of all selected properties is shown under “Cost of withdrawal.” Read more about what the price depends on under `}
        <Anchor href="/docs/property-search/costs">{t`Cost overview`}</Anchor>
        {t`.`}
      </Text>
      <DocImage
        src={{ sv: costSv, en: costEn }}
        alt={t`Screenshot of Cost for withdrawal section`}
        height={200}
      />
    </DocsContent>
  );
}
